import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Radio, RefreshCw, ArrowLeft, Layers, BookOpen, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import OrderModal from '../components/OrderModal';
import FuturesTable from '../components/fo/FuturesTable';
import OptionsTable from '../components/fo/OptionsTable';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const UNDERLYINGS = [
  { key: 'NIFTY50', label: 'NIFTY' },
  { key: 'BANKNIFTY', label: 'BANK NIFTY' },
];

export default function DerivativesPage() {
  const { colors } = useTheme();
  const { token } = useAuth();
  const { prices, simDate, subscribePrices } = useSocket();
  const navigate = useNavigate();

  const [instrumentType, setInstrumentType] = useState('options');
  const [underlying, setUnderlying] = useState('NIFTY50');
  const [futures, setFutures] = useState([]);
  const [futureExpiries, setFutureExpiries] = useState([]);
  const [optionExpiries, setOptionExpiries] = useState([]);
  const [selectedFutureExpiry, setSelectedFutureExpiry] = useState(null);
  const [selectedOptionExpiry, setSelectedOptionExpiry] = useState(null);
  const [optionChain, setOptionChain] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chainLoading, setChainLoading] = useState(false);
  const [orderContract, setOrderContract] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderModalSide, setOrderModalSide] = useState('BUY');
  const [dataAsOf, setDataAsOf] = useState(null);
  const [spotMap, setSpotMap] = useState({});

  const simActive = !!simDate;
  const spot = prices?.[underlying]?.price ?? spotMap[underlying];

  const fetchChain = useCallback(async exp => {
    if (!token || !exp) return;
    setChainLoading(true);
    try {
      const res = await axios.get(`${API_URL}/fo/chain/${underlying}`, {
        params: { expiry: exp },
        headers: { Authorization: `Bearer ${token}` },
      });
      setOptionChain(res.data);
      if (res.data?.data_as_of) setDataAsOf(res.data.data_as_of);
    } catch {
      setOptionChain(null);
    } finally {
      setChainLoading(false);
    }
  }, [token, underlying]);

  // Single bootstrap call replaces 5 parallel HTTP calls (futures, 2×expiries, chain, positions)
  const fetchBootstrap = useCallback(async (signal) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/fo/bootstrap/${underlying}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      const d = res.data;
      const futData = d.futures?.futures ?? [];
      if (d.futures?.data_as_of) setDataAsOf(d.futures.data_as_of);
      else if (d.data_as_of) setDataAsOf(d.data_as_of);

      setFutures(futData);
      setFutureExpiries(d.future_expiries || []);
      setOptionExpiries(d.option_expiries || []);
      setPositions((d.positions || []).filter(p => p.underlying === underlying));

      if (d.selected_future_expiry) setSelectedFutureExpiry(prev => prev || d.selected_future_expiry);
      if (d.selected_option_expiry) {
        setSelectedOptionExpiry(prev => prev || d.selected_option_expiry);
      }
      if (d.option_chain) setOptionChain(d.option_chain);
    } catch (err) {
      if (axios.isCancel(err)) return; // StrictMode cleanup — ignore cancelled request
      setFutures([]);
      setFutureExpiries([]);
      setOptionExpiries([]);
      setPositions([]);
    } finally {
      setLoading(false);
    }
  }, [token, underlying]);

  useEffect(() => {
    const controller = new AbortController();
    fetchBootstrap(controller.signal);
    return () => controller.abort();
  }, [fetchBootstrap]);

  // Re-fetch only when sim_date *changes* (date roll-over). The initial simDate
  // arrival after mount must NOT trigger a second fetch — fetchBootstrap already
  // fired on mount and is still in-flight or just completed.
  const prevSimDateRef = useRef(undefined);
  useEffect(() => {
    if (simDate === undefined) return;
    if (prevSimDateRef.current === undefined) {
      prevSimDateRef.current = simDate;
      return;
    }
    if (simDate !== prevSimDateRef.current) {
      prevSimDateRef.current = simDate;
      setSelectedFutureExpiry(null);
      setSelectedOptionExpiry(null);
      setOptionChain(null);
      fetchBootstrap();
    }
  }, [simDate, fetchBootstrap]);

  // Fetch both spots on mount and whenever sim date rolls over
  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API_URL}/market/sim-price/NIFTY50`, { headers }).catch(() => null),
      axios.get(`${API_URL}/market/sim-price/BANKNIFTY`, { headers }).catch(() => null),
    ]).then(([n, b]) => {
      const map = {};
      if (n?.data?.price) map['NIFTY50'] = n.data.price;
      if (b?.data?.price) map['BANKNIFTY'] = b.data.price;
      setSpotMap(map);
    });
  }, [token, simDate]);

  useEffect(() => {
    setSelectedFutureExpiry(null);
    setSelectedOptionExpiry(null);
    setOptionChain(null);
  }, [underlying]);

  // Subscribe to F&O prices for the selected underlying; leave the room on unmount
  useEffect(() => {
    subscribePrices(`fo:${underlying}`);
    return () => subscribePrices('equities');
  }, [underlying, subscribePrices]);

  // Fetch chain only when the user explicitly changes the expiry AND the chain
  // doesn't already match (bootstrap already loads the default expiry's chain).
  useEffect(() => {
    if (!selectedOptionExpiry) return;
    if (optionChain?.expiry === selectedOptionExpiry) return;
    fetchChain(selectedOptionExpiry);
  }, [selectedOptionExpiry, fetchChain, optionChain?.expiry]);

  const visibleFutures = selectedFutureExpiry
    ? futures.filter(item => item.expiry === selectedFutureExpiry)
    : futures;

  const currentExpiries = instrumentType === 'futures' ? futureExpiries : optionExpiries;
  const cardBg = { background: 'linear-gradient(135deg, rgba(139,92,246,0.10) 0%, rgba(99,102,241,0.03) 100%)', border: '1px solid rgba(139,92,246,0.22)' };
  const sectionHdr = {
    color: colors.secondaryText,
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  };

  const openOrderModal = (contract) => {
    if (!contract) return;
    const livePrice = contract.ltp ?? contract.price ?? prices?.[contract.symbol]?.price ?? null;
    const displayType = contract.type === 'FUT'
      ? 'Future'
      : `${contract.type === 'CE' ? 'Call' : 'Put'} Option`;
    setOrderContract({
      ...contract,
      symbol: contract.symbol,
      company_name: `${contract.underlying} • ${displayType} • ${contract.expiry}${contract.strike ? ` • ${Number(contract.strike).toLocaleString('en-IN')}` : ''}`,
      current_price: livePrice,
      change_pct: prices?.[contract.symbol]?.change_pct ?? contract.change_pct ?? 0,
      contract_id: contract.id,
      is_fo: true,
    });
    setOrderModalSide(contract.side || 'BUY');
    setOrderModalOpen(true);
  };

  return (
    <div className="space-y-4 pb-24" data-testid="derivatives-page">

      {/* Page hero header */}
      <div className="rounded-2xl p-4 relative"
        style={{ background: 'linear-gradient(135deg, #06b6d418 0%, #8b5cf610 100%)', border: '1px solid #06b6d430' }}>
        {/* Sim date — top-right corner */}
        {simDate && (
          <span className="absolute top-3 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-md"
            style={{ backgroundColor: '#8b5cf615', color: '#8b5cf6', border: '1px solid #8b5cf630' }}>
            {simDate}
          </span>
        )}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg"
            style={{ backgroundColor: 'rgba(6,182,212,0.12)', border: '1px solid rgba(6,182,212,0.25)' }}>
            <ArrowLeft className="w-4 h-4" style={{ color: '#06b6d4' }} />
          </button>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #06b6d425, #8b5cf620)', border: '1px solid #06b6d435' }}>
            <Layers className="w-5 h-5" style={{ color: '#06b6d4' }} />
          </div>
          <div>
            <p className="text-[15px] font-bold" style={{ color: colors.primaryText }}>F&amp;O Trading</p>
            <p className="text-[11px]" style={{ color: colors.secondaryText }}>NIFTY &amp; BANKNIFTY futures and options</p>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-3">
          {spotMap['NIFTY50'] != null && (
            <span className="flex items-center gap-1.5">
              <span className="text-[11px]" style={{ color: colors.secondaryText }}>NIFTY Spot</span>
              <span className="text-[13px] font-bold tabular-nums" style={{ color: '#06b6d4' }}>₹{Number(spotMap['NIFTY50']).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
            </span>
          )}
          {spotMap['BANKNIFTY'] != null && (
            <>
              <span style={{ color: colors.border }}>|</span>
              <span className="flex items-center gap-1.5">
                <span className="text-[11px]" style={{ color: colors.secondaryText }}>BANK NIFTY Spot</span>
                <span className="text-[13px] font-bold tabular-nums" style={{ color: '#06b6d4' }}>₹{Number(spotMap['BANKNIFTY']).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
              </span>
            </>
          )}
        </div>
      </div>

      {/* F&O learning nudge */}
      <button
        onClick={() => navigate('/learning?view=level&level=17&card=0')}
        className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left"
        style={{ background: 'linear-gradient(135deg, #8b5cf612 0%, #06b6d408 100%)', border: '1px solid #8b5cf628' }}>
        <div className="flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#8b5cf6' }} />
          <span className="text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
            New to F&amp;O? <span style={{ color: '#8b5cf6' }}>Learn the basics</span>
          </span>
        </div>
        <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#8b5cf650' }} />
      </button>

      {dataAsOf && simDate && dataAsOf !== simDate && (
        <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-medium"
          style={{ background: 'linear-gradient(135deg, #f59e0b12 0%, #f59e0b04 100%)', border: '1px solid #f59e0b30', color: '#f59e0b' }}>
          <span>⚠</span>
          <span>Market closed on sim date — showing last session: <strong>{dataAsOf}</strong></span>
        </div>
      )}
      <div className="rounded-xl p-1.5 space-y-1" style={cardBg}>
        {/* Instrument + Underlying — single compact row */}
        <div className="flex items-center gap-1.5 p-0.5">
          {/* Instrument toggle */}
          <div className="flex p-0.5 rounded-lg flex-1" style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
            {[['futures', 'Future'], ['options', 'Option']].map(([value, label]) => {
              const active = instrumentType === value;
              return (
                <button
                  key={value}
                  onClick={() => setInstrumentType(value)}
                  className="flex-1 py-1.5 rounded-md text-[11px] font-semibold transition-colors"
                  style={{
                    backgroundColor: active ? '#8b5cf6' : 'transparent',
                    color: active ? '#fff' : colors.secondaryText,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="w-px self-stretch" style={{ backgroundColor: colors.border }} />

          {/* Underlying toggle */}
          <div className="flex p-0.5 rounded-lg flex-1" style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
            {UNDERLYINGS.map(item => {
              const active = underlying === item.key;
              const showSpot = spot != null && item.key === underlying;
              return (
                <button
                  key={item.key}
                  onClick={() => setUnderlying(item.key)}
                  className="flex-1 py-1.5 rounded-md text-[11px] font-semibold transition-colors leading-tight"
                  style={{
                    backgroundColor: active ? '#8b5cf6' : 'transparent',
                    color: active ? '#fff' : colors.secondaryText,
                  }}
                >
                  <span className="block">{item.label}</span>
                  {showSpot && (
                    <span className="block text-[9px] font-medium" style={{ opacity: 0.8 }}>
                      ₹{Number(spot).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Expiry */}
        <div>
          <p style={{ ...sectionHdr, fontSize: '0.55rem', marginBottom: '2px' }}>Expiry</p>
          <div
            className="rounded-lg p-1 overflow-x-auto"
            style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}
          >
            <div className="flex gap-1 min-w-max">
              {currentExpiries.map(expiry => {
                const active = instrumentType === 'futures'
                  ? selectedFutureExpiry === expiry
                  : selectedOptionExpiry === expiry;
                return (
                  <button
                    key={expiry}
                    onClick={() => instrumentType === 'futures' ? setSelectedFutureExpiry(expiry) : setSelectedOptionExpiry(expiry)}
                    className="px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors text-center whitespace-nowrap"
                    style={{
                      backgroundColor: active ? '#8b5cf6' : 'transparent',
                      color: active ? '#fff' : colors.secondaryText,
                      border: `1px solid ${active ? '#8b5cf6' : colors.border}`,
                    }}
                  >
                    {expiry}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <p style={sectionHdr}>
            {instrumentType === 'futures' ? 'Futures Table' : 'Options Table'}
          </p>
          <button
            onClick={fetchBootstrap}
            disabled={loading}
            className="p-1.5 rounded-lg transition-colors"
            style={{ background: 'rgba(139,92,246,0.10)', border: '1px solid rgba(139,92,246,0.22)', color: '#8b5cf6' }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {instrumentType === 'futures' ? (
          <FuturesTable contracts={visibleFutures} colors={colors} onTrade={openOrderModal} prices={prices} loading={loading} />
        ) : (
          <OptionsTable chain={optionChain} colors={colors} onTrade={openOrderModal} prices={prices} loading={loading} chainLoading={chainLoading} />
        )}
      </div>

      {positions.length > 0 && (
        <div>
          <p className="mb-2 px-1" style={sectionHdr}>Open Positions</p>
          <div className="space-y-2">
            {positions.map(position => {
              const pnlColor = (position.unrealised_pnl ?? 0) >= 0 ? '#10b981' : '#ef4444';
              return (
                <div key={position.id} className="rounded-xl p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" style={cardBg}>
                  <div className="min-w-0">
                    <p className="text-[10px] mb-0.5" style={{ color: colors.secondaryText }}>
                      {position.type} &middot; {position.expiry} &middot; {position.lot_qty} lot{position.lot_qty > 1 ? 's' : ''}
                    </p>
                    <p className="text-[13px] font-bold break-all" style={{ color: colors.primaryText }}>{position.symbol}</p>
                    <p className="text-[11px]" style={{ color: colors.secondaryText }}>
                      Avg &#8377;{position.entry_price?.toFixed(2)}{position.current_price ? <> &middot; Now &#8377;{position.current_price.toFixed(2)}</> : ''}
                    </p>
                  </div>
                  {position.unrealised_pnl != null && (
                    <div className="text-left sm:text-right">
                      <p className="text-[13px] font-bold" style={{ color: pnlColor }}>
                        {position.unrealised_pnl >= 0 ? '+' : ''}&#8377;{Math.abs(position.unrealised_pnl).toLocaleString('en-IN')}
                      </p>
                      <p className="text-[11px]" style={{ color: pnlColor }}>
                        {position.pnl_pct >= 0 ? '+' : ''}{position.pnl_pct?.toFixed(2)}%
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <OrderModal
        isOpen={orderModalOpen}
        onClose={() => {
          setOrderModalOpen(false);
          setOrderContract(null);
        }}
        stock={orderContract}
        orderType={orderModalSide}
      />
    </div>
  );
}
